import { getTabIndex } from '@/datalayer/App.datalayer.utils';

export enum TabsDLEvents {
  TabClick = 'trackEvent-tabsNavClick',
  CtaClick = 'trackEvent-tabsCtaClick',
  LinkClick = 'trackEvent-tabsLinkClick',
  CarouselNavClick = 'trackEvent-listCardCarouselNavClick',
}

export enum TabsDLEventNames {
  TabClick = 'tabs_nav_click',
  CtaClick = 'tabs_cta_click',
  LinkClick = 'tabs_link_click',
  CarouselNavClick = 'list_card_carousel_nav_click',
}

export enum Selectors {
  // Target
  Tab = 'gtm-tab',
  MUIButton = 'MuiButton-root',
  Scroll = 'gtm-scroll',
}

export interface TabsDLClick {
  event:
    | TabsDLEvents.TabClick
    | TabsDLEvents.CtaClick
    | TabsDLEvents.LinkClick
    | TabsDLEvents.CarouselNavClick;
  event_name:
    | TabsDLEventNames.TabClick
    | TabsDLEventNames.CtaClick
    | TabsDLEventNames.LinkClick
    | TabsDLEventNames.CarouselNavClick;
  title?: string;
  index_num: number | string;
  link_url?: string;
  link_text?: string;
  type?: 'internal' | 'external' | 'file' | 'tab click';
}

type TargetInfo = 'cta' | 'link';

export interface ClickInfo {
  title: string;
  target: HTMLAnchorElement;
  indexNum?: string;
}

interface LinkInfo {
  linkUrl: string;
  linkText: string;
}

const getClickType = (target: HTMLAnchorElement): TabsDLClick['type'] => {
  if (!target.href) {
    return 'tab click';
  }
  const pageHref = window.location.hostname;
  const targetHref = target.href;
  const isInternal = targetHref.includes(pageHref);
  return isInternal ? 'internal' : 'external';
};

const getLinkInfo = ({ href, textContent }: HTMLAnchorElement): LinkInfo => ({
  linkUrl: href || '',
  linkText: textContent || '',
});

const getEvent = (isTab: boolean, isDot: boolean, clickType: TargetInfo): TabsDLClick['event'] => {
  if (isTab) {
    return TabsDLEvents.TabClick;
  }
  if (isDot) {
    return TabsDLEvents.CarouselNavClick;
  }
  return clickType === 'cta' ? TabsDLEvents.CtaClick : TabsDLEvents.LinkClick;
};

const getEventName = (
  isTab: boolean,
  isDot: boolean,
  clickType: TargetInfo,
): TabsDLClick['event_name'] => {
  if (isTab) {
    return TabsDLEventNames.TabClick;
  }
  if (isDot) {
    return TabsDLEventNames.CarouselNavClick;
  }
  return clickType === 'cta' ? TabsDLEventNames.CtaClick : TabsDLEventNames.LinkClick;
};

const checkTarget = (target: HTMLAnchorElement, selector: string) =>
  target?.classList.contains(selector);

const getTargetType = (isCta: boolean) => (isCta ? 'cta' : 'link');

export const getActiveTabIndex = (target: any): number => {
  const carouselId = target.closest('.swiper-container').id;
  return getTabIndex(carouselId) - 1;
};

const getTargetId = (target: HTMLAnchorElement, isDot?: boolean): string => {
  if (isDot) {
    return target?.closest('.swiper-container')?.id || '';
  }

  const elWithTabIndex = target.closest('[role=tab]') || target.closest('[role=tabpanel]');
  if (!elWithTabIndex) return '0-0';
  return elWithTabIndex?.id;
};

export const pushClickToDL = (info: ClickInfo): TabsDLClick => {
  const { title, target, indexNum } = info;

  const isTab = checkTarget(target, Selectors.Tab);
  const isDot = target?.classList.contains(Selectors.Scroll);
  const isCta = checkTarget(target, Selectors.MUIButton);
  const targetId = getTargetId(target, isDot);
  const targetType = getTargetType(isCta);
  const typeOfLink = getClickType(target);
  const { linkUrl, linkText } = getLinkInfo(target);
  const event = getEvent(isTab, isDot, targetType);
  const eventName = getEventName(isTab, isDot, targetType);

  return {
    event,
    event_name: eventName,
    ...(title !== '' && { title }),
    index_num: indexNum || String(getTabIndex(targetId)) || '',
    ...(!isTab && !isDot && { link_url: linkUrl, link_text: linkText, type: typeOfLink }),
  };
};
